import type {
  ComparisonLegendRow,
  KpiFigureContent,
  KpiPairTrend,
  KpiSingleTrend,
} from "@acto/taskpane/field-sales-2/components/primitives/KpiPairCard";
import type { TrendComparisonProps } from "@acto/taskpane/field-sales-2/components/primitives/TrendComparison";
import type { KpiTrend } from "./buildKpiTrend";

const LINE_FIELD = "lineCurrent";
const LINE_PREVIOUS_FIELD = "linePrevious";

export interface KpiPairLabels {
  barKpi: string;
  lineKpi: string;
  currentPeriodLabel: string;
  /** Used for the bars' compact (axis) labels only; full values keep the bar KPI's own formatter. */
  formatBarAxis: (value: number) => string;
  /** Used for the line's compact (axis) labels only; full values keep the line KPI's own formatter. */
  formatLineAxis: (value: number) => string;
  lineTickUnit?: number;
}

const figure = (label: string, total: string, comparison: TrendComparisonProps | undefined): KpiFigureContent => ({
  label,
  value: total,
  previousValue: comparison?.value,
  trendPercent: comparison?.trendPercent ?? null,
});

const legendOf =
  (currentPeriodLabel: string) =>
  (current: string, comparison: TrendComparisonProps | undefined): ComparisonLegendRow[] => [
    { label: currentPeriodLabel, range: current },
    ...(comparison ? [{ label: comparison.label, range: comparison.rangeLabel }] : []),
  ];

export interface KpiSingleLabels {
  kpi: string;
  currentPeriodLabel: string;
  /** Used for the compact (axis) labels only; full values keep the KPI's own formatter. */
  formatAxis: (value: number) => string;
}

export function toKpiSingleTrend(trend: KpiTrend, labels: KpiSingleLabels): KpiSingleTrend {
  const legend = legendOf(labels.currentPeriodLabel);
  const format = trend.chart.valueFormatter ?? ((value: number) => String(value));

  return {
    isPending: trend.isPending,
    isEmpty: trend.isEmpty,
    summary: {
      figure: figure(labels.kpi, trend.total, trend.comparison),
      legend: legend(trend.rangeLabel, trend.comparison),
    },
    buckets: trend.buckets.map((bucket) => ({
      figure: figure(labels.kpi, bucket.total, bucket.comparison),
      legend: legend(bucket.label, bucket.comparison),
    })),
    chart: {
      ...trend.chart,
      leadSeries: { ...trend.chart.leadSeries, label: labels.kpi },
      valueFormatter: (value, { compact }) => (compact ? labels.formatAxis(value) : format(value, { compact })),
    },
  };
}

/**
 * Pairs `bars` and `line` by index, not by period: bucket and chart row i of `line` merge into
 * bucket and row i of `bars`, and a missing `line` entry becomes an empty figure and a gap in the line.
 */
export function toKpiPairTrend(bars: KpiTrend, line: KpiTrend, labels: KpiPairLabels): KpiPairTrend {
  const legend = legendOf(labels.currentPeriodLabel);

  const lineField = line.chart.leadSeries.field;
  const linePreviousField = line.chart.comparisonSeries?.field;
  const formatBar = bars.chart.valueFormatter ?? ((value: number) => String(value));
  const formatLine = line.chart.valueFormatter ?? ((value: number) => String(value));

  return {
    isPending: bars.isPending || line.isPending,
    isEmpty: bars.isEmpty && line.isEmpty,
    summary: {
      figures: [
        figure(labels.barKpi, bars.total, bars.comparison),
        figure(labels.lineKpi, line.total, line.comparison),
      ],
      legend: legend(bars.rangeLabel, bars.comparison),
    },
    buckets: bars.buckets.map((bucket, index) => {
      const lineBucket = line.buckets[index];

      return {
        figures: [
          figure(labels.barKpi, bucket.total, bucket.comparison),
          figure(labels.lineKpi, lineBucket?.total ?? "", lineBucket?.comparison),
        ],
        legend: legend(bucket.label, bucket.comparison),
      };
    }),
    chart: {
      ...bars.chart,
      data: bars.chart.data.map((row, index) => ({
        ...row,
        [LINE_FIELD]: line.chart.data[index]?.[lineField] ?? null,
        ...(linePreviousField ? { [LINE_PREVIOUS_FIELD]: line.chart.data[index]?.[linePreviousField] ?? null } : {}),
      })),
      leadSeries: { ...bars.chart.leadSeries, label: labels.barKpi },
      valueFormatter: (value, { compact }) => (compact ? labels.formatBarAxis(value) : formatBar(value, { compact })),
      lineSeries: {
        leadSeries: { field: LINE_FIELD, label: labels.lineKpi },
        comparisonSeries: line.chart.comparisonSeries && {
          ...line.chart.comparisonSeries,
          field: LINE_PREVIOUS_FIELD,
        },
        valueFormatter: (value, { compact }) =>
          compact ? labels.formatLineAxis(value) : formatLine(value, { compact }),
        tickUnit: labels.lineTickUnit,
      },
    },
  };
}
